import {
  useTreeStore,
  useTreeStoreApi,
  calculateVisibleIds,
} from "@/components/organisms/Tree/Tree.store";
import {
  getGridCellBorderEdges,
  GRID_ROW_CONTAINER_CLASS,
  gridRowContainerStyle,
  useResolvedGridAppearance,
} from "@/components/organisms/Table/Table.tools";
import { Values, ZIndex } from "@/constants";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import { CellProps } from "@/hooks/useContextTree";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import type { TreefetchingOptionsType } from "./index";
import { TreeCell } from "./TreeCell";
import { TreeChevronCell } from "./TreeChevronCell";

// Utility functions for summary nodes
const isSummaryNode = (id: string) => id?.endsWith("-summary") || false;
const getOriginalId = (summaryId: string) => summaryId.replace("-summary", "");

interface TreeRowProps {
  nodeId: string;
  index: number;
  isLoading?: boolean;
  negativeScrollLeft: any;
  columnsLockedLeft: any[];
  columnsUnlocked: any[];
  columnsLockedRight: any[];
  fetchingOptions?: TreefetchingOptionsType;
  isSticky?: boolean;
  stickyIndex?: number;
  isLastRow?: boolean;
  getCellProps?: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  contextId?: string;
}

export function TreeRow({
  nodeId,
  index,
  negativeScrollLeft,
  columnsLockedLeft,
  columnsUnlocked,
  columnsLockedRight,
  fetchingOptions,
  isSticky = false,
  stickyIndex = 0,
  isLastRow = false,
  getCellProps,
  contextId: passedContextId,
}: TreeRowProps) {
  const storeApi = useTreeStoreApi();
  const layout = useTreeStore((state) => state.layout);
  const gridColors = useResolvedGridAppearance(layout?.appearance);
  const storeContextId = useTreeStore((state) => state.contextId);
  const contextId = passedContextId || storeContextId;

  // For sticky rows, look up the actual row index from the full visible list
  // (not visibleRowNodes which is a windowed subset for virtualization)
  // This ensures sticky cells are registered at the correct index for navigation
  const registrationIndex = useTreeStore((state) => {
    if (!isSticky) return index;
    // Calculate the full visible IDs list and find this node's index
    const visibleIds = calculateVisibleIds(state.treeMap, state.layout);
    const actualIndex = visibleIds.indexOf(nodeId);
    return actualIndex >= 0 ? actualIndex : index;
  });

  // Calculate row position
  // For sticky rows: use position: sticky with top offset based on sticky index
  // For regular rows: use position: absolute with top based on index
  const rowTop = index * Values.DEFAULT_ROW_HEIGHT;
  const baseStickyTop = (stickyIndex ?? 0) * Values.DEFAULT_ROW_HEIGHT;

  // Calculate push offset for sticky rows
  // A sticky row should be pushed up when ANY non-descendant row approaches its position
  // This includes siblings, cousins, uncles, etc. - any row with depth <= this row's depth
  const pushOffset = useTreeStore((state) => {
    if (!isSticky) return 0;

    const { treeMap, layout, scrollPosition } = state;
    const currentNode = treeMap[nodeId];
    if (!currentNode) return 0;

    const visibleIds = calculateVisibleIds(treeMap, layout);
    const currentVisibleIndex = visibleIds.indexOf(nodeId);

    if (currentVisibleIndex === -1) return 0;

    // Find the first row AFTER this sticky row that has depth <= this row's depth
    // This is the row that would push this sticky row up (not a descendant)
    let pushingRowIndex = -1;
    for (let i = currentVisibleIndex + 1; i < visibleIds.length; i++) {
      const rowNode = treeMap[visibleIds[i]];
      if (rowNode && rowNode.depth <= currentNode.depth) {
        pushingRowIndex = i;
        break;
      }
    }

    if (pushingRowIndex === -1) return 0; // No row will push this one

    // Calculate viewport position of the pushing row
    // Round to prevent sub-pixel jitter between rows during scrolling
    const pushingRowDocPosition = pushingRowIndex * Values.DEFAULT_ROW_HEIGHT;
    const pushingRowViewportPosition = Math.round(
      pushingRowDocPosition - scrollPosition.y,
    );

    // Push starts when pushing row reaches (baseStickyTop + ROW_HEIGHT)
    // Push ends when pushing row reaches baseStickyTop
    const pushStartPosition = baseStickyTop + Values.DEFAULT_ROW_HEIGHT;
    const pushEndPosition = baseStickyTop;

    if (pushingRowViewportPosition >= pushStartPosition) {
      // Pushing row hasn't approached yet
      return 0;
    } else if (pushingRowViewportPosition <= pushEndPosition) {
      // Pushing row has reached the sticky zone - fully pushed
      return -Values.DEFAULT_ROW_HEIGHT;
    } else {
      // Proportional push as row approaches
      // Round to whole pixels to prevent sub-pixel jitter between rows
      const distanceFromStart = pushStartPosition - pushingRowViewportPosition;
      return -Math.round(distanceFromStart);
    }
  });

  const stickyTop = baseStickyTop + pushOffset;

  let data = null;
  let isLoading = false;

  // Skip data fetching for summary nodes
  const processedId = isSummaryNode(nodeId) ? getOriginalId(nodeId) : nodeId;
  const getChildrenIds = useTreeStore((state) => state.layout?.getChildrenIds);

  // Resolve DYNAMIC fetcher to its concrete inner type. For a given row id this
  // is stable across renders, so the conditional hook calls below stay
  // consistent for that row instance.
  const resolvedFetchingOptions: TreefetchingOptionsType | undefined =
    fetchingOptions?.type === "DYNAMIC"
      ? fetchingOptions.getFetchData(processedId)
      : fetchingOptions;

  if (resolvedFetchingOptions?.type === "QUERY") {
    const { data: queryData, loading: queryLoading } = useQuery(
      resolvedFetchingOptions.query,
      {
        variables: resolvedFetchingOptions.prepQuery(processedId),
        // notifyOnNetworkStatusChange: true,
      },
    );
    data = resolvedFetchingOptions?.parseQuery?.(queryData) || queryData;
    isLoading = queryLoading;
  } else if (resolvedFetchingOptions?.type === "LOCAL") {
    data = useMemo(
      () => resolvedFetchingOptions.getData(processedId),
      [resolvedFetchingOptions, processedId],
    );
  }

  const [visibleStartColumnIndex, visibleEndColumnIndex] = useTreeStore(
    useShallow((state) => [
      state.visibleStartColumnIndex,
      state.visibleEndColumnIndex,
    ]),
  );

  // Load and update node data when available
  useEffect(() => {
    if (!data || !getChildrenIds) return;
    storeApi.getState().updateNodeData(nodeId, data, getChildrenIds);
  }, [data, nodeId, getChildrenIds]);

  const onRowClick = useTreeStore((state) => state.layout?.onRowClick);
  const hideOuterBorders = useTreeStore(
    (state) => state.layout?.hideOuterBorders ?? false,
  );
  const rowBottomEdge = getGridCellBorderEdges({
    color: "var(--border)",
    showRightEdge: false,
    isOuterBottom: isLastRow,
    hideOuterEdges: hideOuterBorders,
  });

  const rowRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={rowRef}
      key={nodeId}
      onClick={() => onRowClick?.(nodeId)}
      className={GRID_ROW_CONTAINER_CLASS}
      style={{
        ...gridRowContainerStyle(gridColors),
        zIndex: isSticky
          ? ZIndex.GRID.STICKY_CONTAINER - stickyIndex
          : ZIndex.GRID.ROW,
        ...rowBottomEdge,
        width: `var(--${contextId}-total-width)`,
        height: Values.DEFAULT_ROW_HEIGHT,
        position: isSticky ? "sticky" : "absolute",
        left: 0,
        top: isSticky ? stickyTop : rowTop,
        cursor: onRowClick ? "pointer" : undefined,
      }}
    >
      {/* Row - Locked Left (includes chevron cell at column 0) */}
      <div
        style={{
          position: "absolute",
          left: 0,
          zIndex: isSticky
            ? ZIndex.GRID.TREE_STICKY_LOCKED - stickyIndex
            : ZIndex.GRID.TREE_ROW_LOCKED,
        }}
      >
        {/* Chevron cell at column index 0 */}
        {(() => {
          const chevronCellProps = getCellProps?.({
            rowIndex: registrationIndex,
            colIndex: 0,
            objectKey: "__chevron",
          });
          return (
            <TreeChevronCell
              nodeId={nodeId}
              rowIndex={registrationIndex}
              isSticky={isSticky}
              isLoading={isLoading}
              isLastRow={isLastRow}
              cellProps={chevronCellProps}
              contextId={contextId}
            />
          );
        })()}
        {/* Locked left columns - shifted by 1 to account for chevron column */}
        {columnsLockedLeft.map((col, lockedIndex) => {
          // Column index is shifted by 1 because chevron is at index 0
          const colIndex = lockedIndex + 1;
          const cellProps = getCellProps?.({
            rowIndex: registrationIndex,
            colIndex: colIndex,
            objectKey: col.objectKey,
          });

          return (
            <TreeCell
              key={lockedIndex + col.id}
              col={col}
              nodeId={nodeId}
              data={data}
              isHeader={false}
              isSticky={isSticky}
              isLoading={isLoading || col.isLoading === true}
              isLastColumn={false}
              isLastRow={isLastRow}
              isFirstLockedColumn={lockedIndex === 0}
              rowIndex={registrationIndex}
              colIndex={colIndex}
              cellProps={cellProps}
              contextId={contextId}
            />
          );
        })}
      </div>

      {/* Row - Scrollable */}
      <motion.div
        style={{
          position: "absolute",
          left: 0,
          x: negativeScrollLeft,
          zIndex: isSticky
            ? ZIndex.GRID.TREE_STICKY_SCROLLABLE - stickyIndex
            : ZIndex.GRID.TREE_ROW_SCROLLABLE,
          ...rowBottomEdge,
        }}
      >
        {columnsUnlocked.map((col, unlockedIndex) => {
          if (
            unlockedIndex < visibleStartColumnIndex ||
            unlockedIndex > visibleEndColumnIndex
          ) {
            return null;
          }

          // Column index: +1 for chevron + locked left columns + unlocked index
          const colIndex = 1 + columnsLockedLeft.length + unlockedIndex;
          const cellProps = getCellProps?.({
            rowIndex: registrationIndex,
            colIndex: colIndex,
            objectKey: col.objectKey,
          });

          return (
            <TreeCell
              key={unlockedIndex + col.id}
              col={col}
              nodeId={nodeId}
              data={data}
              isHeader={false}
              isSticky={isSticky}
              isLoading={isLoading || col.isLoading === true}
              isLastColumn={
                columnsLockedRight.length === 0 &&
                unlockedIndex === columnsUnlocked.length - 1
              }
              isLastRow={isLastRow}
              isFirstLockedColumn={false}
              rowIndex={registrationIndex}
              colIndex={colIndex}
              cellProps={cellProps}
              contextId={contextId}
            />
          );
        })}
      </motion.div>

      {/* Row - Locked Right */}
      <div
        style={{
          position: "absolute",
          left: 0,
          zIndex: isSticky
            ? ZIndex.GRID.TREE_STICKY_LOCKED - stickyIndex
            : ZIndex.GRID.TREE_ROW_LOCKED,
          ...rowBottomEdge,
        }}
      >
        {columnsLockedRight.map((col, rightIndex) => {
          // Column index: +1 for chevron + locked left + unlocked + right index
          const colIndex =
            1 + columnsLockedLeft.length + columnsUnlocked.length + rightIndex;
          const cellProps = getCellProps?.({
            rowIndex: registrationIndex,
            colIndex: colIndex,
            objectKey: col.objectKey,
          });

          return (
            <TreeCell
              key={rightIndex + col.id}
              col={col}
              nodeId={nodeId}
              data={data}
              isHeader={false}
              isSticky={isSticky}
              isLoading={isLoading || col.isLoading === true}
              isLastColumn={rightIndex === columnsLockedRight.length - 1}
              isLastRow={isLastRow}
              isFirstLockedColumn={false}
              rowIndex={registrationIndex}
              colIndex={colIndex}
              cellProps={cellProps}
              contextId={contextId}
            />
          );
        })}
      </div>
    </div>
  );
}
